import {
  AHIcon,
  AHText,
  Box,
  OrgIcon,
  ScaleFactorProvider,
  SubjectBreadcrumbs,
  useScaleFactor,
  useTheme,
} from "arkh";
import React, { Suspense, useEffect, useMemo } from "react";
import { MasonryImageGrid } from "../../../common/MasonryImageGrid";
import { useFragment, graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useSubscriptionWithSkip } from "../../../../../widgets/src/utils/useSubscriptionWithSkip";
import { SignalDetailSubscription } from "./__generated__/SignalDetailSubscription.graphql";

import { SignalDetailImages_signal$key } from "./__generated__/SignalDetailImages_signal.graphql";
import { SignalDetailQuery } from "./__generated__/SignalDetailQuery.graphql";
import { RichTextViewer } from "@/src/components/RichText";
import { SignalDetailContent_signal$key } from "./__generated__/SignalDetailContent_signal.graphql";
import { SignalDetailContentMain_signal$key } from "./__generated__/SignalDetailContentMain_signal.graphql";
import { StoreFormattedDate } from "../../../../../mobile/src/common/dates/StoreFormattedDate";
import { SignalDetailFooterTV_signal$key } from "./__generated__/SignalDetailFooterTV_signal.graphql";
import { SignalDetailRegisterAsSeenMutation } from "./__generated__/SignalDetailRegisterAsSeenMutation.graphql";
import { ScrollView } from "react-native";
import { SignalDetailFiles } from "./SignalDetailFiles";
import { LinkedProducts } from "../../../common/LinkedProducts";
import { FormattedMessage } from "react-intl";

export function SignalDetail(props: {
  signalId: string;
  storecastId: string;
}) {
  const result = useLazyLoadQuery<SignalDetailQuery>(
    graphql`
      query SignalDetailQuery($signalId: ID!) {
        signal(id: $signalId) {
          ...SignalDetailContent_signal
        }
      }
    `,
    {
      signalId: props.signalId,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );
  const signal = result.signal;

  useSubscriptionWithSkip<SignalDetailSubscription>(
    useMemo(
      () => ({
        variables: { signalIds: [props.signalId] },
        subscription: graphql`
          subscription SignalDetailSubscription($signalIds: [ID!]!) {
            signalChangedByIds(signalIds: $signalIds) {
              ...SignalDetailContent_signal
            }
          }
        `,
      }),
      [props.signalId]
    )
  );

  if (!signal) {
    return null;
  }

  return (
    <SignalDetailContent
      signal={signal}
      storecastId={props.storecastId}
    />
  );
}

function SignalDetailContent(props: {
  signal: SignalDetailContent_signal$key;
  storecastId: string;
}) {
  const sc = useScaleFactor();
  const theme = useTheme();

  const _signal = useFragment(
    graphql`
      fragment SignalDetailContent_signal on Signal {
        ...SignalDetailContentMain_signal
        ...SignalDetailFooterTV_signal
        ...SignalDetailImages_signal
      }
    `,
    props.signal
  );

  const initialLoadedSignal = useFragment<SignalDetailContentMain_signal$key>(
    graphql`
      fragment SignalDetailContentMain_signal on Signal {
        id
        title
        formattedBody
        body
        orgIconUri
        breadcrumbs
        updatedAtDate {
          ...StoreFormattedDate
        }
        descriptionFiles {
          ...SignalDetailFiles_files
        }
        linkedProducts {
          ...LinkedProducts_products
        }
        ...SignalDetailImages_signal
      }
    `,
    _signal
  );

  const [registerAsSeen] = useMutation<SignalDetailRegisterAsSeenMutation>(
    graphql`
      mutation SignalDetailRegisterAsSeenMutation($signalId: ID!) {
        signal(id: $signalId) {
          registerEmployeeReceiverReceipt {
            id
            opened
            employeeReceiverReceiptsCount
            employeeReceiverReceipts {
              signalWorkspaceId
              employeeId
              openedAtDate {
                ...FormattedDate
              }
            }
          }
        }
      }
    `
  );

  useEffect(() => {
    registerAsSeen({
      variables: {
        signalId: initialLoadedSignal.id,
      },
    });
  }, [initialLoadedSignal.id, registerAsSeen]);

  const hasFormattedBody = useMemo(() => {
    return (
      initialLoadedSignal.formattedBody &&
      initialLoadedSignal.formattedBody.length > 0 &&
      initialLoadedSignal.formattedBody !==
      '{"type":"doc","content":[{"type":"paragraph"}]}'
    );
  }, [initialLoadedSignal.formattedBody]);

  return (
    <Box style={{ flex: 1, flexDirection: "column" }}>
      <Box
        style={{ flex: 1, flexDirection: "row" }}
        spaceHorizontal="medium"
        spaceVertical="medium"
      >
        <Box
          style={{
            width: "45%",
          }}
        >
          <ScaleFactorProvider value={sc}>
            <Box
              color="surface-primary"
              borderColor="border-primary"
              style={{
                borderWidth: 1,
                flexDirection: "column",
                flex: 1,
              }}
              cornerRadius="large"
            >
              <Box
                style={{ flexDirection: "row", alignItems: "center" }}
                spaceHorizontal="medium"
                spaceTop="medium"
              >
                <OrgIcon
                  spaceRight="xsmall"
                  orgIconUri={initialLoadedSignal.orgIconUri}
                />
                <SubjectBreadcrumbs
                  breadcrumbs={initialLoadedSignal.breadcrumbs}
                />
                <Box style={{ flex: 1 }} />
                <AHText type="detail" color="text-washed" spaceRight="small">
                  <StoreFormattedDate date={initialLoadedSignal.updatedAtDate} />
                </AHText>
                <AHIcon type="locked" size="xsmall" color="text-washed" />
              </Box>

              <Box space="small" spaceHorizontal="medium">
                <AHText type="title-small" color="text-primary">
                  {initialLoadedSignal.title}
                </AHText>
              </Box>


              <ScrollView style={{ flex: 1 }}>
                {initialLoadedSignal.body && !hasFormattedBody ? (
                  <Box spaceHorizontal="medium" spaceVertical="medium">
                    <AHText type="body-big" color="text-secondary">
                      {initialLoadedSignal.body}
                    </AHText>
                  </Box>
                ) : null}

                {hasFormattedBody ? (
                  <Box
                    spaceHorizontal="medium"
                    spaceVertical="medium"

                  >
                    <RichTextViewer data={initialLoadedSignal.formattedBody} />
                  </Box>
                ) : null}

                <SignalDetailFiles
                  files={initialLoadedSignal.descriptionFiles}
                />
                <LinkedProducts products={initialLoadedSignal.linkedProducts} />
              </ScrollView>

              <Suspense fallback={null}>
                <Footer signal={_signal} />
              </Suspense>
            </Box>
          </ScaleFactorProvider>
        </Box>
        <SignalDetailImages signal={initialLoadedSignal} />
      </Box>
    </Box>
  );
}

function Footer(props: { signal: SignalDetailFooterTV_signal$key }) {
  const signal = useFragment(
    graphql`
      fragment SignalDetailFooterTV_signal on Signal {
        id
        employeeReceiverReceiptsCount
        store {
          id
          employeeCount
        }
      }
    `,
    props.signal
  );

  return (
    <Box spaceTop="medium" spaceBottom="medium" spaceHorizontal="medium">
      <Box
        style={{
          height: 40,
          flexDirection: "row",
          alignItems: "center",
          alignSelf: "flex-end",
        }}
        spaceHorizontal="small"
      >
        <AHIcon type="eye" color="text-secondary" spaceRight="small" />
        <AHText type="subtitle" color="text-secondary" selectable={false}>
          {signal.employeeReceiverReceiptsCount}/{signal.store.employeeCount}{" "}
          <FormattedMessage
            defaultMessage="Employees"
            description="SignalDetail employee count label"
          />
        </AHText>
      </Box>
    </Box>
  );
}

function SignalDetailImages(props: { signal: SignalDetailImages_signal$key }) {
  const signal = useFragment(
    graphql`
      fragment SignalDetailImages_signal on Signal {
        descriptionImages {
          id
          size(minHeight: 699, minWidth: 699) {
            url
          }
        }
      }
    `,
    props.signal
  );

  const images = useMemo(
    () =>
      signal.descriptionImages.flatMap((image) =>
        image.size?.url ? [{ url: image.size.url }] : []
      ),
    [signal.descriptionImages]
  );

  return images.length > 0 ? (
    <Box style={{ flex: 1 }} spaceLeft="medium">
      <MasonryImageGrid images={images} />
    </Box>
  ) : null;
}
